import React, {useEffect, useRef, useState} from "react";
import {KeenIcon, Scrollspy} from "@/components";
import {Modal, ModalContent, ModalBody, ModalHeader} from "@/components/modal";
import {useResponsive, useViewport} from "@/hooks";
import {JobSelection} from "./JobSelection";
import {JobDetailsData} from "@/pages/buddy/types/JobDetailsData";
import {UploadDocument} from "@/pages/buddy/shared/new-soruce/UploadDocument";
import {SidebarModalMenu} from "@/pages/buddy/shared/new-soruce/SidebarModelMenu";
import {Intro} from "@/pages/buddy/candidate/blocks/source/Intro";
import {CandidateDetailsData} from "@/pages/buddy/types/CandidateDetailsData";
import {UploadText} from "@/pages/buddy/shared/new-soruce/UploadText.tsx";
import {CrawlWebpage} from "@/pages/buddy/shared/new-soruce/CrawlWebpage.tsx";
import {CrawlLinkedIn} from "@/pages/buddy/shared/new-soruce/CrawlLinkedIn.tsx";

interface CandidateModalProps {
    open: boolean;
    onClose: () => void;
    mode: "create" | "addSource";
    preselectedJob?: JobDetailsData | null;
    candidate?: CandidateDetailsData | null;
    refreshCandidateData?: () => void;
}

const CandidateModal: React.FC<CandidateModalProps> = ({
                                                           open,
                                                           onClose,
                                                           mode,
                                                           preselectedJob = null,
                                                           candidate = null,
                                                           refreshCandidateData,
                                                       }) => {
    // Determine mode
    const isCreateMode = mode === "create";
    const isAddSourceMode = mode === "addSource";

    // In creation mode, allow for an optional job selection.
    const [selectedJob, setSelectedJob] = useState<JobDetailsData | null>(
        isCreateMode ? preselectedJob : null
    );

    // Responsive layout and sidebar configuration.
    const desktopMode = useResponsive("up", "lg");
    const navBar = useRef<any | null>(null);
    const parentRef = useRef<any | null>(null);
    const [sidebarHeight, setSidebarHeight] = useState<number>(0);
    const [viewportHeight] = useViewport();
    const offset = 260;

    useEffect(() => {
        setSidebarHeight(viewportHeight - offset);
    }, [viewportHeight]);

    // Generate a title for the modal based on mode.
    const getTitle = (): string => {
        if (isCreateMode) {
            return selectedJob
                ? `Add New Candidate Profile for ${selectedJob.name}`
                : "Add New Candidate Profile";
        }
        if (isAddSourceMode && candidate) {
            return `Add New Source to Candidate ${candidate.first_name} ${candidate.last_name}`;
        }
        return "Candidate";
    };

    // Create a parent entity based on mode.
    const parentEntity =
        isCreateMode
            ? selectedJob
            : candidate
                ? {
                    p_id: candidate.p_id,
                    // Use candidate.name if available, or candidate.fullName, or fallback.
                    name: (candidate as any).name || (candidate as any).fullName || "Unknown Candidate",
                }
                : null;

    return (
        <Modal open={open} onClose={onClose} className="!flex">
            <ModalContent className="container-fixed px-30 overflow-hidden pt-7.5 my-[3%] w-full">
                <ModalHeader className="p-0 border-0">
                    <div className="flex items-center justify-between flex-wrap grow gap-5 pb-7.5">
                        <div className="flex items-baseline space-x-2">
                            <KeenIcon icon="user" className="text-3xl"/>
                            <h1 className="text-xl font-semibold leading-none text-gray-900">{getTitle()}</h1>
                        </div>
                        <div className="flex items-center gap-3">
                            {/* In creation mode, allow job change if one is selected */}
                            {isCreateMode && selectedJob && (
                                <button
                                    className="btn btn-sm btn-light text-gray-600 hover:text-primary"
                                    onClick={() => setSelectedJob(null)}
                                >
                                    Change Job
                                </button>
                            )}
                            <button className="btn btn-sm btn-light" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </ModalHeader>

                <ModalBody className="scrollable-y py-0 mb-5 ps-0 pe-3 -me-5.5" ref={parentRef}>
                    <div className="flex grow gap-5 lg:gap-7.5">
                        {/* Sidebar for desktop view */}
                        {desktopMode && (
                            <div className="block w-[230px] shrink-0">
                                <div
                                    ref={navBar}
                                    className="w-[230px] fixed z-10 scrollable-y-auto"
                                    style={{maxHeight: `${sidebarHeight}px`}}
                                >
                                    <Scrollspy offset={100} targetRef={parentRef}>
                                        <SidebarModalMenu/>
                                    </Scrollspy>
                                </div>
                            </div>
                        )}
                        <div className="flex flex-col items-stretch grow gap-5 lg:gap-7.5">
                            {/* If in creation mode without a job selected, show JobSelection; otherwise, show UploadDocument */}
                            {isCreateMode && !selectedJob ? (
                                <JobSelection onSelectJob={setSelectedJob}/>
                            ) : (
                                <>
                                    <Intro mode={mode}/>
                                    <UploadDocument
                                        parentEntity={parentEntity}
                                        entityType="candidate"
                                        mode={mode}
                                        onSuccess={refreshCandidateData}
                                    />
                                    <UploadText
                                        parentEntity={parentEntity}
                                        entityType="candidate"
                                        mode={mode}
                                        onSuccess={refreshCandidateData}
                                    />
                                    <CrawlWebpage
                                        parentEntity={parentEntity}
                                        entityType="candidate"
                                        mode={mode}
                                        onSuccess={refreshCandidateData}
                                    />
                                    <CrawlLinkedIn />
                                </>
                            )}
                        </div>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export {CandidateModal};
